import Phaser from 'phaser';
import { BOSSES } from '../config/bosses';
import { GameState, DIFFICULTY_MODS } from '../state/GameState';
import { SaveStore } from '../state/SaveStore';
import { ANIMAL_STATS } from '../config/animals';
import { makeMountWithRider } from '../render/mount';
import { makeBoss } from '../render/bosses';
import { MAX_LEVEL } from '../config/levels';
import type { BossConfig } from '../types';

type Phase = 'menu' | 'reveal' | 'over';
type Move = 'attack' | 'defend' | 'heal';

const ATTACK_COLOR = 0xc23b3b;
const DEFEND_COLOR = 0x4a8acc;
const HEAL_COLOR = 0x3a8a4a;

export class BossScene extends Phaser.Scene {
  private level = 5;
  private boss!: BossConfig;

  private playerHp = 0;
  private playerMaxHp = 0;
  private playerHeals = 0;
  private bossHp = 0;
  private bossMaxHp = 0;
  private bossHealsLeft = 5;
  private phase: Phase = 'menu';

  private playerHpText!: Phaser.GameObjects.Text;
  private bossHpText!: Phaser.GameObjects.Text;
  private playerHpBar!: Phaser.GameObjects.Rectangle;
  private bossHpBar!: Phaser.GameObjects.Rectangle;
  private playerHpBarMax = 360;
  private bossHpBarMax = 360;
  private logText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;

  private bossSprite!: Phaser.GameObjects.Container;
  private playerSprite!: Phaser.GameObjects.Container;

  private menuButtons: Phaser.GameObjects.Container[] = [];
  // Per-round picks (shown over each fighter when revealed)
  private playerPickIcon?: Phaser.GameObjects.Container;
  private bossPickIcon?: Phaser.GameObjects.Container;

  constructor() {
    super('BossScene');
  }

  init(data: { level?: number }): void {
    this.level = data.level ?? 5;
    const trigger = this.level as 5 | 10 | 15 | 20;
    this.boss = BOSSES[trigger];

    // Track attempts on this boss level so the SKIP button only unlocks
    // after 5 tries (entries to BossScene).
    const key = `bossAttempts_${this.level}`;
    const prior = (this.registry.get(key) as number | undefined) ?? 0;
    this.registry.set(key, prior + 1);

    const animalKey = GameState.getAnimal(this.registry);
    const stats = ANIMAL_STATS[animalKey];
    this.playerMaxHp = stats.hp * 10 + 18 + this.level;
    this.playerHp = this.playerMaxHp;
    this.playerHeals = Math.max(0, GameState.getRunStats(this.registry).fruitsThisLevel);
    // Boss HP scales gently with level + global difficulty.
    const t = Math.min(1, (this.level - 5) / 20);
    const diffHp = DIFFICULTY_MODS[GameState.getDifficulty(this.registry)].bossHpMult;
    // Last two bosses (15 and 20) get an extra HP boost.
    const lateBossBonus = this.level === 15 ? 1.25 : this.level === 20 ? 1.5 : 1;
    this.bossMaxHp = Math.round(this.boss.hp * 0.75 * (1 + 0.25 * t) * diffHp * lateBossBonus);
    this.bossHp = this.bossMaxHp;
    this.bossHealsLeft = 5;
    this.phase = 'menu';
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;

    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1d2434, 0x1d2434, 0x402610, 0x402610, 1);
    bg.fillRect(0, 0, W, H);

    this.add
      .text(W / 2, 40, `Boss Battle — ${this.boss.displayName}`, {
        fontFamily: 'sans-serif',
        fontSize: '32px',
        color: '#f2c14e',
      })
      .setOrigin(0.5);

    // Boss sprite (right) — flipped so it faces the player.
    this.bossSprite = makeBoss(this, this.boss.key, W - 280, 280);
    this.bossSprite.setScale(1.4);
    this.tweens.add({
      targets: this.bossSprite,
      y: 270,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Player sprite (left)
    const character = GameState.getCharacter(this.registry);
    const animalKey = GameState.getAnimal(this.registry);
    this.playerSprite = makeMountWithRider(this, animalKey, character, 280, 360);
    this.playerSprite.setScale(2.4);

    // HP bars
    this.add.text(40, 90, 'YOU', { fontFamily: 'sans-serif', fontSize: '18px', color: '#fff' });
    this.add
      .rectangle(40, 120, this.playerHpBarMax, 20, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xffffff);
    this.playerHpBar = this.add
      .rectangle(40, 120, this.playerHpBarMax, 20, 0x4ea36b)
      .setOrigin(0, 0);
    this.playerHpText = this.add.text(40, 144, '', {
      fontFamily: 'sans-serif',
      fontSize: '16px',
      color: '#fff',
    });

    this.add.text(W - 40 - this.bossHpBarMax, 90, this.boss.displayName, {
      fontFamily: 'sans-serif',
      fontSize: '18px',
      color: '#f2c14e',
    });
    this.add
      .rectangle(W - 40 - this.bossHpBarMax, 120, this.bossHpBarMax, 20, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xffffff);
    this.bossHpBar = this.add
      .rectangle(W - 40 - this.bossHpBarMax, 120, this.bossHpBarMax, 20, 0xc23b3b)
      .setOrigin(0, 0);
    this.bossHpText = this.add.text(W - 40 - this.bossHpBarMax, 144, '', {
      fontFamily: 'sans-serif',
      fontSize: '16px',
      color: '#fff',
    });

    // Battle log
    this.logText = this.add
      .text(W / 2, H - 240, 'Pick your move — both fighters reveal at the same time.', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffd9a0',
        align: 'center',
        wordWrap: { width: 760 },
      })
      .setOrigin(0.5);

    // How-to-play hint
    this.hintText = this.add
      .text(
        W / 2,
        H - 200,
        'ATTACK beats HEAL  •  HEAL beats DEFEND  •  DEFEND beats ATTACK',
        { fontFamily: 'sans-serif', fontSize: '16px', color: '#bcd0e0' },
      )
      .setOrigin(0.5);

    // Skip button — only available after 5 attempts on this boss.
    const attempts = (this.registry.get(`bossAttempts_${this.level}`) as number | undefined) ?? 1;
    const skipUnlocked = attempts >= 5;
    const skipBtn = this.add.container(W - 24, 18).setDepth(20);
    const skipBg = this.add
      .rectangle(0, 0, 130, 32, skipUnlocked ? 0x3d2914 : 0x666666, 0.85)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(1, 0);
    const skipTxt = this.add
      .text(-65, 16, skipUnlocked ? 'SKIP ▶' : `Try ${attempts}/5`, {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0.5);
    skipBtn.add([skipBg, skipTxt]);
    if (skipUnlocked) {
      skipBg.setInteractive({ useHandCursor: true });
      skipBg.on('pointerover', () => skipBg.setFillStyle(0x6b3410, 0.9));
      skipBg.on('pointerout', () => skipBg.setFillStyle(0x3d2914, 0.85));
      skipBg.on('pointerdown', () => {
        if (this.phase === 'over') return;
        this.bossHp = 0;
        this.victory();
      });
    }

    this.makeMenu();
    this.refreshHud();
  }

  private makeMenu(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.menuButtons.forEach((b) => b.destroy());
    this.menuButtons = [];
    const opts: Array<{ move: Move; label: string; color: number; sub: string }> = [
      { move: 'attack', label: '⚔  Attack', color: ATTACK_COLOR, sub: 'beats Heal' },
      { move: 'defend', label: '🛡  Defend', color: DEFEND_COLOR, sub: 'beats Attack' },
      { move: 'heal', label: '💚 Heal', color: HEAL_COLOR, sub: `beats Defend (${this.playerHeals} fruit)` },
    ];
    opts.forEach((opt, i) => {
      const x = W / 2 + (i - 1) * 220;
      const y = H - 90;
      const btn = this.add.container(x, y);
      const bg = this.add.rectangle(0, 0, 200, 84, opt.color).setStrokeStyle(3, 0xffffff);
      const t1 = this.add
        .text(0, -14, opt.label, { fontFamily: 'sans-serif', fontSize: '24px', color: '#ffffff' })
        .setOrigin(0.5);
      const t2 = this.add
        .text(0, 16, opt.sub, { fontFamily: 'sans-serif', fontSize: '13px', color: '#ffffff' })
        .setOrigin(0.5);
      btn.add([bg, t1, t2]);
      btn.setSize(200, 84).setInteractive({ useHandCursor: true });
      btn.on('pointerover', () => bg.setStrokeStyle(4, 0xffd24a));
      btn.on('pointerout', () => bg.setStrokeStyle(3, 0xffffff));
      btn.on('pointerdown', () => {
        if (this.phase !== 'menu') return;
        if (opt.move === 'heal' && this.playerHeals <= 0) {
          this.log('No fruit left to heal with.');
          return;
        }
        this.playRound(opt.move);
      });
      this.menuButtons.push(btn);
    });
  }

  private playRound(playerMove: Move): void {
    this.phase = 'reveal';
    // Boss picks at the same time. Lightly bias based on its config so each
    // boss has a flavour, but RPS is the core resolver.
    const bossMove = this.bossPickMove();

    // Show "VS" reveal animation: icons pop above each fighter.
    this.playerPickIcon = this.makePickIcon(playerMove, this.playerSprite.x, this.playerSprite.y - 140);
    this.bossPickIcon = this.makePickIcon(bossMove, this.bossSprite.x, this.bossSprite.y - 110);

    this.log(`You chose ${labelFor(playerMove)}. ${this.boss.displayName} chose ${labelFor(bossMove)}.`);

    this.time.delayedCall(900, () => {
      this.resolveRound(playerMove, bossMove);
    });
  }

  private bossPickMove(): Move {
    // Less aggressive than before to make fights easier.
    // Level 5 → ~28% attack, level 25 → ~40% attack.
    const t = Math.min(1, (this.level - 5) / 20);
    const attack = 0.28 + 0.12 * t;
    const defend = (1 - attack) / 2;
    const r = Math.random();
    if (r < attack) return 'attack';
    if (r < attack + defend) return 'defend';
    if (this.bossHealsLeft <= 0) return r < attack + defend + 0.5 ? 'attack' : 'defend';
    return 'heal';
  }

  private resolveRound(p: Move, b: Move): void {
    // Easier scaling: boss damage starts at 70% of base and rises to ~95% at L25.
    const t = Math.min(1, (this.level - 5) / 20);
    const diffDmg = DIFFICULTY_MODS[GameState.getDifficulty(this.registry)].bossDmgMult;
    const lateBossDmg = this.level === 15 ? 1.2 : this.level === 20 ? 1.4 : 1;
    const bossScale = (0.7 + 0.25 * t) * diffDmg * lateBossDmg;
    const playerAtk = this.boss.playerAttackDamage + Phaser.Math.Between(-2, 6);
    const baseBossAtk = this.averageBossDamage();
    const bossAtk = Math.max(2, Math.round(baseBossAtk * bossScale) + Phaser.Math.Between(-3, 3));
    const healAmount = 16;

    let result = '';
    if (p === b) {
      // Tie — small effect for both
      if (p === 'attack') {
        const half = Math.round(playerAtk / 2);
        const halfB = Math.round(bossAtk / 2);
        this.bossHp = Math.max(0, this.bossHp - half);
        this.playerHp = Math.max(0, this.playerHp - halfB);
        result = `Both attacked! ${half} to boss, ${halfB} to you.`;
        this.flash(this.bossSprite);
        this.flash(this.playerSprite);
      } else if (p === 'defend') {
        result = 'Both defended. Nothing happens.';
      } else {
        const heal = Math.round(healAmount / 2);
        this.playerHeals = Math.max(0, this.playerHeals - 1);
        this.playerHp = Math.min(this.playerMaxHp, this.playerHp + heal);
        this.bossHp = Math.min(this.bossMaxHp, this.bossHp + heal);
        this.bossHealsLeft = Math.max(0, this.bossHealsLeft - 1);
        result = `Both healed for ${heal}.`;
      }
    } else if (beats(p, b)) {
      // Player wins the round
      if (p === 'attack') {
        this.bossHp = Math.max(0, this.bossHp - playerAtk);
        result = `Your attack landed for ${playerAtk}!`;
        this.flash(this.bossSprite);
      } else if (p === 'defend') {
        // Defend beats attack — player takes no damage
        result = 'You blocked the boss attack!';
      } else {
        // Heal beats defend
        this.playerHeals = Math.max(0, this.playerHeals - 1);
        this.playerHp = Math.min(this.playerMaxHp, this.playerHp + healAmount);
        result = `You healed ${healAmount} HP while it defended.`;
      }
    } else {
      // Boss wins the round
      if (b === 'attack') {
        this.playerHp = Math.max(0, this.playerHp - bossAtk);
        result = `${this.boss.displayName} attacked for ${bossAtk}!`;
        this.flash(this.playerSprite);
      } else if (b === 'defend') {
        result = `${this.boss.displayName} blocked your attack.`;
      } else {
        const heal = healAmount;
        this.bossHp = Math.min(this.bossMaxHp, this.bossHp + heal);
        this.bossHealsLeft = Math.max(0, this.bossHealsLeft - 1);
        result = `${this.boss.displayName} healed ${heal} HP while you defended.`;
      }
    }

    this.log(result);
    this.refreshHud();

    // Clear pick icons.
    this.time.delayedCall(900, () => {
      this.playerPickIcon?.destroy();
      this.bossPickIcon?.destroy();
      this.playerPickIcon = undefined;
      this.bossPickIcon = undefined;

      if (this.bossHp <= 0) return this.victory();
      if (this.playerHp <= 0) return this.defeat();

      this.makeMenu();
      this.phase = 'menu';
    });
  }

  private averageBossDamage(): number {
    const total = this.boss.attacks.reduce((s, a) => s + a.damage * a.weight, 0);
    const totalW = this.boss.attacks.reduce((s, a) => s + a.weight, 0);
    return Math.round(total / totalW);
  }

  private makePickIcon(move: Move, x: number, y: number): Phaser.GameObjects.Container {
    const c = this.add.container(x, y).setDepth(25);
    const color = move === 'attack' ? ATTACK_COLOR : move === 'defend' ? DEFEND_COLOR : HEAL_COLOR;
    const bg = this.add.circle(0, 0, 30, color).setStrokeStyle(3, 0xffffff);
    const txt = this.add
      .text(0, 0, iconFor(move), { fontFamily: 'sans-serif', fontSize: '32px', color: '#ffffff' })
      .setOrigin(0.5);
    c.add([bg, txt]);
    c.setScale(0);
    this.tweens.add({ targets: c, scale: 1, duration: 250, ease: 'Back.easeOut' });
    return c;
  }

  private victory(): void {
    this.phase = 'over';
    this.log(`You defeated the ${this.boss.displayName}!`);
    this.menuButtons.forEach((b) => b.destroy());
    this.menuButtons = [];
    this.tweens.add({
      targets: this.bossSprite,
      alpha: 0,
      angle: 30,
      y: '+=80',
      duration: 1200,
    });
    this.registry.set(`bossAttempts_${this.level}`, 0);
    const next = this.level + 1;
    GameState.setLevel(this.registry, next);
    SaveStore.save({
      character: GameState.getCharacter(this.registry),
      animal: GameState.getAnimal(this.registry),
      level: next,
    });
    this.time.delayedCall(1600, () => {
      if (this.level >= MAX_LEVEL) {
        this.scene.start('WinScene');
      } else {
        this.scene.start('LevelIntroScene', { level: next });
      }
    });
  }

  private defeat(): void {
    this.phase = 'over';
    this.menuButtons.forEach((b) => b.destroy());
    this.menuButtons = [];
    this.log('You were defeated...');
    this.time.delayedCall(1100, () => {
      this.scene.start('GameOverScene', { level: this.level });
    });
  }

  private flash(target: Phaser.GameObjects.Container): void {
    this.tweens.add({
      targets: target,
      alpha: 0.4,
      duration: 80,
      yoyo: true,
      repeat: 2,
    });
  }

  private log(msg: string): void {
    this.logText.setText(msg);
  }

  private refreshHud(): void {
    this.playerHpBar.width = (this.playerHp / this.playerMaxHp) * this.playerHpBarMax;
    this.bossHpBar.width = (this.bossHp / this.bossMaxHp) * this.bossHpBarMax;
    this.playerHpText.setText(`HP: ${this.playerHp}/${this.playerMaxHp}`);
    this.bossHpText.setText(`HP: ${this.bossHp}/${this.bossMaxHp}`);
    if (this.hintText) {
      // Refresh hint copy if heal counter changed
      this.hintText.setText('ATTACK beats HEAL  •  HEAL beats DEFEND  •  DEFEND beats ATTACK');
    }
  }
}

function beats(a: Move, b: Move): boolean {
  // attack > heal, heal > defend, defend > attack
  return (
    (a === 'attack' && b === 'heal') ||
    (a === 'heal' && b === 'defend') ||
    (a === 'defend' && b === 'attack')
  );
}

function labelFor(m: Move): string {
  return m === 'attack' ? 'Attack' : m === 'defend' ? 'Defend' : 'Heal';
}

function iconFor(m: Move): string {
  return m === 'attack' ? '⚔' : m === 'defend' ? '🛡' : '💚';
}
